/**
 * Authentication Service for Adobe IMS OAuth
 *
 * Handles OAuth Authorization Code flow with PKCE for SPAs.
 * Based on the mcp-bridge implementation pattern.
 */

import { OAUTH_CONFIG, STORAGE_KEYS, IMS_ENDPOINTS } from '../config/config';
import { ImsUserProfile } from '../types';
import { sha256 } from 'js-sha256';

const OAUTH_STATE_KEY = 'oauth_state';
const OAUTH_VERIFIER_KEY = 'oauth_code_verifier';

const OAUTH_QUERY_KEYS = ['code', 'state', 'error', 'error_description'] as const;

/** Dedupes concurrent callback handling (e.g. React Strict Mode double-mount). */
let oauthCallbackInFlight: Promise<boolean> | null = null;

/**
 * Remove OAuth callback query parameters from the current URL without navigation.
 */
export function clearOAuthCallbackFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  if (!OAUTH_QUERY_KEYS.some((key) => params.has(key))) {
    return;
  }
  for (const key of OAUTH_QUERY_KEYS) {
    params.delete(key);
  }
  const search = params.toString();
  const next = search
    ? `${window.location.pathname}?${search}`
    : window.location.pathname;
  window.history.replaceState(null, '', next);
}

/**
 * Check if user has a valid (non-expired) token.
 * Token is considered invalid if it's missing or acquired more than 7 hours ago.
 */
export function hasValidToken(): boolean {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const acquiredAtStr = localStorage.getItem(STORAGE_KEYS.TOKEN_ACQUIRED_AT);

  if (!token || !acquiredAtStr) {
    return false;
  }

  const acquiredAt = parseInt(acquiredAtStr, 10);
  const now = Date.now();
  const elapsed = now - acquiredAt;

  // Token is invalid if acquired more than 7 hours ago
  return elapsed < OAUTH_CONFIG.TOKEN_EXPIRY_THRESHOLD_MS;
}

/**
 * Get the current access token if valid
 */
export function getAccessToken(): string | null {
  if (!hasValidToken()) {
    return null;
  }
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
}

/**
 * Save token data to local storage
 */
export function saveToken(accessToken: string): void {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.TOKEN_ACQUIRED_AT, Date.now().toString());
}

function clearOAuthSessionKeys(): void {
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_VERIFIER_KEY);
}

/**
 * Clear all local auth data.
 */
export function clearAuthData(): void {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.TOKEN_ACQUIRED_AT);
  localStorage.removeItem(STORAGE_KEYS.SELECTED_ORG);
  clearOAuthSessionKeys();
}

/**
 * Perform federated logout: clear local state, then redirect to Adobe IMS
 * logout endpoint so the IMS session cookie is also invalidated.
 */
export function federatedLogout(): void {
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  clearAuthData();
  clearOAuthCallbackFromUrl();

  if (IMS_ENDPOINTS.LOGOUT && accessToken) {
    const params = new URLSearchParams({
      access_token: accessToken,
      redirect_uri: OAUTH_CONFIG.REDIRECT_URI,
    });
    window.location.href = `${IMS_ENDPOINTS.LOGOUT}?${params.toString()}`;
  } else {
    window.location.href = OAUTH_CONFIG.REDIRECT_URI;
  }
}

/**
 * Generate a random string for state/PKCE
 */
function generateRandomString(length: number = 32): string {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Convert Uint8Array to base64url string
 */
function uint8ArrayToBase64Url(array: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < array.length; i++) {
    binary += String.fromCharCode(array[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generate PKCE code verifier (43-128 characters)
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return uint8ArrayToBase64Url(array);
}

/**
 * Generate PKCE code challenge from verifier (SHA-256 hash, base64url encoded)
 * Falls back to js-sha256 when crypto.subtle is unavailable (non-HTTPS contexts)
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  // Check if crypto.subtle is available (requires HTTPS or localhost)
  if (window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return uint8ArrayToBase64Url(new Uint8Array(digest));
  }

  // Fallback for non-secure contexts (HTTP)
  const hashArray = sha256.array(verifier);
  return uint8ArrayToBase64Url(new Uint8Array(hashArray));
}

/**
 * Store OAuth state and code verifier for verification after callback
 */
function storeOAuthParams(state: string, codeVerifier: string): void {
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  sessionStorage.setItem(OAUTH_VERIFIER_KEY, codeVerifier);
}

/**
 * Atomically validate OAuth state and consume the PKCE verifier from session storage.
 */
function takeOAuthSession(expectedState: string | null): {
  stateMatch: boolean;
  codeVerifier: string | null;
} {
  const storedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  const verifier = sessionStorage.getItem(OAUTH_VERIFIER_KEY);

  if (!expectedState || !storedState || expectedState !== storedState || !verifier) {
    return { stateMatch: false, codeVerifier: null };
  }

  clearOAuthSessionKeys();
  return { stateMatch: true, codeVerifier: verifier };
}

/**
 * Initiate OAuth login flow using Authorization Code with PKCE
 */
export async function initiateLogin(): Promise<void> {
  clearOAuthCallbackFromUrl();
  clearOAuthSessionKeys();

  const state = generateRandomString();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  storeOAuthParams(state, codeVerifier);

  const params = new URLSearchParams({
    client_id: OAUTH_CONFIG.CLIENT_ID,
    response_type: 'code',
    scope: OAUTH_CONFIG.SCOPES.join(' '),
    redirect_uri: OAUTH_CONFIG.REDIRECT_URI,
    state: state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    dctx_id: OAUTH_CONFIG.DCTX_ID,
  });

  const authUrl = `${IMS_ENDPOINTS.AUTHORIZE}?${params.toString()}`;
  window.location.href = authUrl;
}

/**
 * Check if current URL has OAuth callback parameters
 */
export function hasOAuthCallback(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
}

/**
 * Handle OAuth callback - exchange authorization code for tokens
 * Returns true if token was successfully obtained
 */
export async function handleOAuthCallback(): Promise<boolean> {
  if (!hasOAuthCallback()) {
    return false;
  }

  if (oauthCallbackInFlight) {
    return oauthCallbackInFlight;
  }

  oauthCallbackInFlight = runOAuthCallback().finally(() => {
    oauthCallbackInFlight = null;
  });

  return oauthCallbackInFlight;
}

async function runOAuthCallback(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');
  const errorDescription = params.get('error_description');

  if (error) {
    console.error('OAuth error:', error, errorDescription);
    clearOAuthCallbackFromUrl();
    clearOAuthSessionKeys();
    throw new Error(`OAuth error: ${error} - ${errorDescription || 'Unknown error'}`);
  }

  if (!code) {
    return false;
  }

  // Stale callback URL while already signed in (back button, org switch, duplicate mount).
  if (hasValidToken()) {
    clearOAuthCallbackFromUrl();
    clearOAuthSessionKeys();
    return false;
  }

  const { stateMatch, codeVerifier } = takeOAuthSession(state);
  if (!stateMatch || !codeVerifier) {
    console.warn('OAuth state mismatch or missing PKCE session — sign in again');
    clearOAuthCallbackFromUrl();
    clearOAuthSessionKeys();
    throw new Error(
      'Your sign-in session expired or was interrupted. Please click Sign in with Adobe to try again.',
    );
  }

  try {
    const tokenResponse = await exchangeCodeForToken(code, codeVerifier);

    // Fresh login defaults to the first org from the up-to-date IMS profile.
    localStorage.removeItem(STORAGE_KEYS.SELECTED_ORG);

    saveToken(tokenResponse.access_token);
    clearOAuthCallbackFromUrl();
    return true;
  } catch (err) {
    clearOAuthCallbackFromUrl();
    clearOAuthSessionKeys();
    throw err;
  }
}

/**
 * Exchange authorization code for access token
 */
async function exchangeCodeForToken(
  code: string,
  codeVerifier: string,
): Promise<{ access_token: string }> {
  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: OAUTH_CONFIG.CLIENT_ID,
    code: code,
    redirect_uri: OAUTH_CONFIG.REDIRECT_URI,
    code_verifier: codeVerifier,
  });

  const response = await fetch(IMS_ENDPOINTS.TOKEN, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('Token exchange failed:', errorData);
    throw new Error(
      `Token exchange failed: ${errorData.error_description || errorData.error || response.status}`,
    );
  }

  return response.json();
}

/**
 * Fetch user profile from Adobe IMS
 */
export async function fetchUserProfile(accessToken: string): Promise<ImsUserProfile> {
  const response = await fetch(IMS_ENDPOINTS.PROFILE, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      client_id: OAUTH_CONFIG.CLIENT_ID,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user profile: ${response.status}`);
  }

  const profile: ImsUserProfile = await response.json();
  return profile;
}
